import React, { useState, useCallback, useEffect } from "react";
import FloatingCard from "../../../ui/FloatingCard";
import ScoringModeToggle from "../../live/components/ScoringModeToggle";
import PermissionBadge from "../common/PermissionBadge";
import BasicScoreBox from "../common/BasicScoreBox";
import InlinePlayerGrid from "../common/InlinePlayerGrid";
import { getVolleyballFormat } from "../../../../utils/sportRules";

// ─── localStorage helpers ────────────────────────────────────────────────────
const VB_KEY = (id) => `volleyball_sets_${id}`;
const saveVB = (id, data) => { try { localStorage.setItem(VB_KEY(id), JSON.stringify(data)); } catch {} };
const loadVB = (id) => { try { const r = localStorage.getItem(VB_KEY(id)); return r ? JSON.parse(r) : null; } catch { return null; } };

/**
 * Read volleyball game configuration from the match object.
 *
 * Match creation saves points/margin under match_config.scoring_rules and the
 * number of sets under match_config.game_rules; schedule-generated and older
 * records may keep everything in game_rules — both are read.
 *
 * FIVB: sets to 25, win by 2, no cap. The deciding set (set 5 of 5, and
 * set 3 of 3) is played to 15.
 */
const getVBConfig = (match) => {
  const { totalSets, ptsToWinSet, winMargin, setsToWin, finalSetPts } = getVolleyballFormat(match);
  return { totalSets, ptsToWinSet, winMargin, setsToWin, finalSetPts };
};

// Exported so unit tests can verify config parsing without mounting the component
export { getVBConfig };

const VolleyballScoreboard = ({
  match,
  currentScore,
  isReadOnlyMode,
  connected,
  isMatchActive,
  onScoreIncrease,
  onScoreDecrease,
  onResetScore,
  onResetSetScore,
  onCompleteMatch,
  onStartMatch,
  isTimeoutActive,
  onToggleTimeout,
  matchDuration,
  scoringMode,
  onScoringModeChange,
  advancedSupported = false,
  announcerMuted,
  onAnnouncerToggleMute,
  onAnnouncerRepeat,
  p1Players = [],
  p2Players = [],
  onPlayerSelect,
  // Set state sync (LS-B07): onGameComplete reports a finished set so it is
  // stored with the score and broadcast; externalGameState is the set state
  // held by the server (page load, another scorer, viewers).
  onGameComplete,
  externalGameState,
}) => {
  const matchId = match?.id;
  const { totalSets, ptsToWinSet, winMargin, setsToWin, finalSetPts } = getVBConfig(match);
  const isSingleSet = totalSets === 1;

  // ── Set state (persisted per match) ──────────────────────────────────────
  const [setsWon, setSetsWon] = useState(() => {
    const saved = loadVB(matchId);
    return saved?.setsWon ?? { p1: 0, p2: 0 };
  });
  const [setScores, setSetScores] = useState(() => {
    const saved = loadVB(matchId);
    return saved?.setScores ?? []; // [{p1, p2}, ...]
  });
  const [currentSet, setCurrentSet] = useState(() => {
    const saved = loadVB(matchId);
    // Clamp: older saved state could hold "set 6 of 5" after completion.
    return Math.min(saved?.currentSet ?? 1, totalSets);
  });
  const [matchWon, setMatchWon] = useState(() => {
    const saved = loadVB(matchId);
    return saved?.matchWon ?? false;
  });

  // null = live current set; N = inspecting historical set N
  const [viewingSet, setViewingSet] = useState(null);

  // Server set state wins over this browser's copy (reload, second device, viewers)
  useEffect(() => {
    if (!externalGameState || externalGameState.currentGame === undefined) return;
    const next = {
      setsWon: externalGameState.gamesWon ?? { p1: 0, p2: 0 },
      setScores: externalGameState.gameScores ?? [],
      currentSet: Math.min(externalGameState.currentGame, totalSets),
      matchWon: externalGameState.matchWon ?? false,
    };
    setSetsWon(next.setsWon);
    setSetScores(next.setScores);
    setCurrentSet(next.currentSet);
    setMatchWon(next.matchWon);
    saveVB(matchId, next);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalGameState]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const isFinalSet = currentSet === totalSets;
  const ptsCap     = isFinalSet ? finalSetPts : ptsToWinSet;
  const p1SetScore = currentScore?.participant1 || 0;
  const p2SetScore = currentScore?.participant2 || 0;

  const checkSetWon = useCallback((p1, p2) => {
    if (p1 === p2) return null;
    const ahead = p1 > p2 ? "p1" : "p2";
    if (Math.max(p1, p2) < ptsCap) return null;
    // No cap in volleyball: play continues until one side leads by the margin
    if (Math.abs(p1 - p2) < Math.max(1, winMargin)) return null;
    return ahead;
  }, [ptsCap, winMargin]);

  const setWinner = checkSetWon(p1SetScore, p2SetScore);

  // ── Names ─────────────────────────────────────────────────────────────────
  const dit    = (match?.matchConfig || match?.match_config)?.drop_in_teams;
  const p1Name = dit ? (dit.team_a?.name || "Team A") : (match?.participant_1_name || match?.participant1Name || "Team 1");
  const p2Name = dit ? (dit.team_b?.name || "Team B") : (match?.participant_2_name || match?.participant2Name || "Team 2");

  // "p1" | "p2" | "draw" for each completed set
  const setWinners = setScores.map((ss) =>
    ss.p1 > ss.p2 ? "p1" : ss.p2 > ss.p1 ? "p2" : "draw"
  );

  // ── Set completion handler ────────────────────────────────────────────────
  const handleCompleteSet = () => {
    // Match already decided (e.g. the confirm dialog was cancelled): re-open
    // the completion dialog instead of counting the final set twice.
    if (matchWon) {
      const data = buildCurrentCompletionData();
      if (data && onCompleteMatch) onCompleteMatch(data);
      return;
    }
    if (!setWinner) return;
    const newSetsWon = {
      p1: setsWon.p1 + (setWinner === "p1" ? 1 : 0),
      p2: setsWon.p2 + (setWinner === "p2" ? 1 : 0),
    };
    const newSetScores  = [...setScores, { p1: p1SetScore, p2: p2SetScore }];
    const newMatchWon   = newSetsWon.p1 >= setsToWin || newSetsWon.p2 >= setsToWin;
    // Never advance past the last set — prevents "Set 6 of 5" after completion.
    const newCurrentSet = newMatchWon ? currentSet : Math.min(currentSet + 1, totalSets);

    setSetsWon(newSetsWon);
    setSetScores(newSetScores);
    setCurrentSet(newCurrentSet);
    setMatchWon(newMatchWon);
    saveVB(matchId, {
      setsWon: newSetsWon,
      setScores: newSetScores,
      currentSet: newCurrentSet,
      matchWon: newMatchWon,
    });
    // Stored with the next score write and broadcast to other devices
    if (onGameComplete) {
      onGameComplete({
        gamesWon: newSetsWon,
        gameScores: newSetScores,
        currentGame: newCurrentSet,
        matchWon: newMatchWon,
      });
    }

    // Use the raw (no-modal) reset so transitioning between sets is seamless.
    // The final set's score stays on the board once the match is won.
    if (!newMatchWon) {
      const resetFn = onResetSetScore || onResetScore;
      if (resetFn) resetFn({ eventType: "game_start", description: `Set ${newCurrentSet} started`, period: newCurrentSet });
    }

    if (newMatchWon) {
      const matchWinnerSide = newSetsWon.p1 >= setsToWin ? "p1" : "p2";
      const winnerName      = matchWinnerSide === "p1" ? p1Name : p2Name;
      const p1Id   = match?.participant1Id   || match?.participant_1_id;
      const p2Id   = match?.participant2Id   || match?.participant_2_id;
      const p1Type = match?.participant1Type || match?.participant_1_type || "team";
      const p2Type = match?.participant2Type || match?.participant_2_type || "team";

      const completionData = {
        cancelled: false,
        winner: {
          id:   matchWinnerSide === "p1" ? p1Id   : p2Id,
          type: matchWinnerSide === "p1" ? p1Type : p2Type,
        },
        winnerName,
        participant1Score: newSetsWon.p1,
        participant2Score: newSetsWon.p2,
        gameScores: newSetScores.map((s) => ({ p1: s.p1, p2: s.p2 })),
      };

      if (onCompleteMatch) onCompleteMatch(completionData);
    }
  };

  /**
   * Build completionData from current set state for manual "Complete" button.
   * A set that is won but not yet completed counts, so pressing Complete right
   * after the deciding point records the sets (3–0), not that set's points (25–0).
   */
  const buildCurrentCompletionData = () => {
    const countCurrent = !matchWon && (setWinner === "p1" || setWinner === "p2");
    const won = countCurrent
      ? { p1: setsWon.p1 + (setWinner === "p1" ? 1 : 0), p2: setsWon.p2 + (setWinner === "p2" ? 1 : 0) }
      : setsWon;
    const scores = countCurrent ? [...setScores, { p1: p1SetScore, p2: p2SetScore }] : setScores;
    const matchWinnerSide = won.p1 >= setsToWin ? "p1"
                          : won.p2 >= setsToWin ? "p2"
                          : null;
    if (!matchWinnerSide) return null;
    const winnerName = matchWinnerSide === "p1" ? p1Name : p2Name;
    const p1Id   = match?.participant1Id   || match?.participant_1_id;
    const p2Id   = match?.participant2Id   || match?.participant_2_id;
    const p1Type = match?.participant1Type || match?.participant_1_type || "team";
    const p2Type = match?.participant2Type || match?.participant_2_type || "team";
    return {
      cancelled: false,
      winner: {
        id:   matchWinnerSide === "p1" ? p1Id   : p2Id,
        type: matchWinnerSide === "p1" ? p1Type : p2Type,
      },
      winnerName,
      participant1Score: won.p1,
      participant2Score: won.p2,
      gameScores: scores.map((s) => ({ p1: s.p1, p2: s.p2 })),
    };
  };

  // ── +1 / -1 / inline-player controls ────────────────────────────────────
  const isAdvancedInline = scoringMode === 'advanced' && advancedSupported && !!onPlayerSelect;

  const addBlocked = !isMatchActive || !!setWinner || matchWon || viewingSet !== null;
  const p1SubBlocked = !isMatchActive || matchWon || p1SetScore <= 0 || viewingSet !== null;
  const p2SubBlocked = !isMatchActive || matchWon || p2SetScore <= 0 || viewingSet !== null;

  // Called as a function (not <ScoreControls/>) so the player icons are not
  // remounted on every render — the action popover stays anchored to them.
  const renderScoreControls = (participant, score, players) => {
    if (isReadOnlyMode) return null;
    const subBlocked = !isMatchActive || matchWon || score <= 0 || viewingSet !== null;

    if (isAdvancedInline) {
      return (
        <div className="vb-adv-inline">
          <InlinePlayerGrid participant={participant} players={players} isDisabled={addBlocked} onPlayerSelect={onPlayerSelect} />
          <button
            className="vb-deduct-btn"
            onClick={() => onScoreDecrease(participant, 1)}
            disabled={subBlocked}
          >
            −1
          </button>
        </div>
      );
    }

    // Basic mode is rendered via the clickable BasicScoreBox in the main JSX below
    return null;
  };

  const viewingScore = viewingSet !== null ? setScores[viewingSet - 1] : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <FloatingCard variant="gradient" size="large" className="score-display-websocket delay-3" glow={isMatchActive}>

      {/* Header */}
      <div className="score-header-websocket">
        <div className="score-header-top">
          <h3>
            🏐 Volleyball
            {!isSingleSet && (
              <span style={{ fontWeight: 400, color: "#94a3b8", fontSize: "0.88em" }}>
                {" — "}
                {viewingSet !== null
                  ? `Set ${viewingSet} (history)`
                  : `Set ${currentSet} of ${totalSets}`}
              </span>
            )}
          </h3>
          <div className="score-header-top-right">
            {!isReadOnlyMode && onScoringModeChange && (
              <ScoringModeToggle
                mode={scoringMode || "basic"}
                onChange={onScoringModeChange}
                sportSupported={advancedSupported}
                disabled={!isMatchActive}
              />
            )}
            <PermissionBadge isReadOnlyMode={isReadOnlyMode} />
          </div>
        </div>

        {/* Score Announcer pill — shown to editors and viewers alike; viewers
            default to muted and can opt in via the mute toggle. */}
        {isMatchActive && onAnnouncerToggleMute && (
          <div className="score-announcer-bar">
            <button
              className={`announcer-btn${announcerMuted ? ' muted' : ''}`}
              onClick={onAnnouncerToggleMute}
              title={announcerMuted ? 'Unmute score announcer' : 'Mute score announcer'}
              aria-label={announcerMuted ? 'Unmute score announcer' : 'Mute score announcer'}
            >
              {announcerMuted ? '🔇' : '🔊'}
            </button>
            <span className="announcer-label">Score Announcer</span>
            <button
              className="announcer-btn"
              onClick={onAnnouncerRepeat}
              title="Repeat last score announcement"
              aria-label="Repeat last score announcement"
            >
              🔁
            </button>
          </div>
        )}

        {!isReadOnlyMode && (
          <div className="live-match-quick-controls">
            {match?.status === "scheduled" && onStartMatch && (
              <button className="live-ctrl-btn live-ctrl-start" onClick={onStartMatch}>
                🎬 Start Match
              </button>
            )}
            {isMatchActive && (
              <>
                <button
                  className={`live-ctrl-btn live-ctrl-timeout${isTimeoutActive ? " active" : ""}`}
                  onClick={onToggleTimeout}
                >
                  {isTimeoutActive ? "▶️ Resume" : "⏸️ Timeout"}
                </button>
                <button className="live-ctrl-btn live-ctrl-reset" onClick={onResetScore}>
                  🔄 Reset Set
                </button>
                <button className="live-ctrl-btn live-ctrl-complete" onClick={() => onCompleteMatch && onCompleteMatch(buildCurrentCompletionData() || undefined)}>
                  🏁 Complete
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Set navigator tabs (multi-set only) */}
      {!isSingleSet && (
      <div style={{
        display: "flex", gap: "0.35rem", justifyContent: "center", alignItems: "center",
        marginBottom: "0.45rem", flexWrap: "wrap", padding: "0 0.25rem",
      }}>
        <span style={{ fontSize: "0.72rem", color: "#64748b", marginRight: "0.15rem" }}>Sets:</span>

        {Array.from({ length: totalSets }, (_, i) => {
          const setNum    = i + 1;
          const completed = setNum < currentSet || (matchWon && setNum <= setScores.length);
          const live      = setNum === currentSet;
          const future    = setNum > currentSet;
          const ss        = setScores[i];
          const winner    = setWinners[i];
          const isViewing = viewingSet === setNum;

          if (future) {
            return (
              <span key={setNum} style={{ padding: "0.18rem 0.45rem", borderRadius: "0.3rem", background: "rgba(0,0,0,0.15)", color: "#475569", fontSize: "0.68rem", border: "1px solid rgba(71,85,105,0.2)" }}>
                S{setNum}
              </span>
            );
          }

          if (completed) {
            const dotColor = winner === "p1" ? "#22c55e" : winner === "p2" ? "#ef4444" : "#f59e0b";
            return (
              <button
                key={setNum}
                title={`Set ${setNum}: ${p1Name} ${ss?.p1}–${ss?.p2} ${p2Name}`}
                onClick={() => setViewingSet(isViewing ? null : setNum)}
                style={{
                  padding: "0.18rem 0.5rem", borderRadius: "0.3rem",
                  background: isViewing ? "rgba(99,102,241,0.3)" : "rgba(0,0,0,0.25)",
                  border: `1px solid ${isViewing ? "rgba(99,102,241,0.6)" : "rgba(100,116,139,0.4)"}`,
                  color: isViewing ? "#a78bfa" : "#94a3b8",
                  fontSize: "0.68rem", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: "0.2rem",
                }}
              >
                <span style={{ color: dotColor, fontSize: "0.55rem" }}>●</span>
                S{setNum} {ss?.p1}–{ss?.p2}
                {isViewing && <span> ✕</span>}
              </button>
            );
          }

          // Current live set
          return (
            <button
              key={setNum}
              title="Current live set — click to return to live view"
              onClick={() => setViewingSet(null)}
              style={{
                padding: "0.18rem 0.5rem", borderRadius: "0.3rem",
                background: viewingSet === null ? "rgba(34,197,94,0.2)" : "rgba(0,0,0,0.2)",
                border: `1px solid ${viewingSet === null ? "rgba(34,197,94,0.5)" : "rgba(100,116,139,0.3)"}`,
                color: viewingSet === null ? "#22c55e" : "#94a3b8",
                fontSize: "0.68rem", cursor: "pointer",
                fontWeight: viewingSet === null ? 700 : 400,
                display: "flex", alignItems: "center", gap: "0.2rem",
              }}
            >
              {viewingSet === null && <span style={{ color: "#ef4444", fontSize: "0.5rem" }}>●</span>}
              S{setNum}
              {viewingSet !== null && <span style={{ fontSize: "0.55rem", opacity: 0.7 }}> ▶</span>}
            </button>
          );
        })}

        <span style={{ marginLeft: "0.4rem", fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 }}>
          {setsWon.p1}–{setsWon.p2}
        </span>
      </div>
      )}

      {/* Rules line */}
      <div style={{ textAlign: "center", fontSize: "0.68rem", color: "#64748b", marginBottom: "0.65rem" }}>
        {isFinalSet ? `Final set — first to ${finalSetPts} pts` : `First to ${ptsCap} pts (by ${winMargin})`}
        {!isSingleSet && <>{" · "}{setsToWin} sets to win</>}
      </div>

      {/* Historical set detail */}
      {viewingSet !== null && viewingScore ? (
        <div style={{ padding: "1rem 1.25rem", background: "rgba(0,0,0,0.2)", borderRadius: "0.5rem", marginBottom: "0.75rem" }}>
          <div style={{ textAlign: "center", color: "#64748b", fontSize: "0.75rem", marginBottom: "0.65rem" }}>
            Set {viewingSet} — Final Score
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "2rem" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#e2e8f0", fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.25rem" }}>{p1Name}</div>
              <div style={{ fontSize: "3rem", fontWeight: 800, lineHeight: 1, color: viewingScore.p1 > viewingScore.p2 ? "#22c55e" : "#94a3b8" }}>
                {viewingScore.p1}
              </div>
              {viewingScore.p1 > viewingScore.p2 && (
                <div style={{ fontSize: "0.65rem", color: "#22c55e", marginTop: "0.2rem" }}>✓ Won Set</div>
              )}
            </div>
            <div style={{ fontSize: "1.5rem", color: "#334155", fontWeight: 700 }}>–</div>
            <div style={{ textAlign: "center" }}>
              <div style={{ color: "#e2e8f0", fontWeight: 600, fontSize: "0.85rem", marginBottom: "0.25rem" }}>{p2Name}</div>
              <div style={{ fontSize: "3rem", fontWeight: 800, lineHeight: 1, color: viewingScore.p2 > viewingScore.p1 ? "#22c55e" : "#94a3b8" }}>
                {viewingScore.p2}
              </div>
              {viewingScore.p2 > viewingScore.p1 && (
                <div style={{ fontSize: "0.65rem", color: "#22c55e", marginTop: "0.2rem" }}>✓ Won Set</div>
              )}
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: "0.85rem" }}>
            <button
              onClick={() => setViewingSet(null)}
              style={{ padding: "0.3rem 0.9rem", fontSize: "0.75rem", background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.4)", color: "#22c55e", borderRadius: "0.4rem", cursor: "pointer" }}
            >
              ▶ Back to Live Scoring
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Set won banner (only when not viewing history) */}
          {setWinner && isMatchActive && !isReadOnlyMode && (
            <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap", padding: "0.75rem 1rem", background: "rgba(34,197,94,0.1)", border: "1px solid rgba(34,197,94,0.4)", borderRadius: "0.5rem", marginBottom: "0.75rem" }}>
              <span style={{ color: "#22c55e", fontWeight: 700 }}>
                🏆 {setWinner === "p1" ? p1Name : p2Name} won Set {currentSet}!
                {" "}({p1SetScore}–{p2SetScore})
              </span>
              {!matchWon && currentSet < totalSets && setsWon[setWinner] + 1 < setsToWin ? (
                <button
                  onClick={handleCompleteSet}
                  style={{ padding: "0.4rem 1.1rem", background: "rgba(34,197,94,0.25)", border: "1px solid #22c55e", color: "#22c55e", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}
                >
                  ✅ Complete Set {currentSet} — Start Set {currentSet + 1}
                </button>
              ) : (
                <button
                  onClick={handleCompleteSet}
                  style={{ padding: "0.4rem 1.1rem", background: "rgba(34,197,94,0.25)", border: "1px solid #22c55e", color: "#22c55e", borderRadius: "0.4rem", cursor: "pointer", fontWeight: 600 }}
                >
                  🏆 Complete Match
                </button>
              )}
            </div>
          )}

          {/* Live scoring display — single grid row keeps VS centred between both cards */}
          <div className="participants-websocket">
            <div className="vb-participant-col">
              <div className="vb-team-name">{p1Name}</div>
              {!isReadOnlyMode && !isAdvancedInline ? (
                <BasicScoreBox
                  participantClassName="participant-1"
                  score={p1SetScore}
                  disabled={addBlocked}
                  subDisabled={p1SubBlocked}
                  onIncrease={() => onScoreIncrease("participant1", 1)}
                  onDecrease={() => onScoreDecrease("participant1", 1)}
                  addLabel={`Add 1 point for ${p1Name}`}
                  subLabel={`Subtract 1 point for ${p1Name}`}
                >
                  {!isSingleSet && <div className="participant-type">Sets Won: {setsWon.p1}</div>}
                </BasicScoreBox>
              ) : (
                <div className="participant-websocket participant-1">
                  {!isSingleSet && <div className="participant-type">Sets Won: {setsWon.p1}</div>}
                  <div className="participant-score-large">{p1SetScore}</div>
                  {renderScoreControls("participant1", p1SetScore, p1Players)}
                </div>
              )}
            </div>

            <div className="vs-divider-websocket">
              <div className="vs-circle-websocket">{isSingleSet ? "VS" : `S${currentSet}`}</div>
              <div className="vs-duration" style={{ fontSize: "0.85rem", color: "#64748b", whiteSpace: "nowrap", textAlign: "center" }}>
                {isMatchActive
                  ? "In Progress"
                  : match?.status === "scheduled"
                    ? "Scheduled"
                    : (match?.status?.replace(/_/g, " ") ?? "—")}
              </div>
              {matchDuration && (
                <div style={{ fontSize: "0.85rem", fontWeight: 600, color: isTimeoutActive ? "#f59e0b" : "#64748b", whiteSpace: "nowrap", textAlign: "center", marginTop: "0.2rem" }}>
                  ⏱ {matchDuration}
                </div>
              )}
            </div>

            <div className="vb-participant-col">
              <div className="vb-team-name">{p2Name}</div>
              {!isReadOnlyMode && !isAdvancedInline ? (
                <BasicScoreBox
                  participantClassName="participant-2"
                  score={p2SetScore}
                  disabled={addBlocked}
                  subDisabled={p2SubBlocked}
                  onIncrease={() => onScoreIncrease("participant2", 1)}
                  onDecrease={() => onScoreDecrease("participant2", 1)}
                  addLabel={`Add 1 point for ${p2Name}`}
                  subLabel={`Subtract 1 point for ${p2Name}`}
                >
                  {!isSingleSet && <div className="participant-type">Sets Won: {setsWon.p2}</div>}
                </BasicScoreBox>
              ) : (
                <div className="participant-websocket participant-2">
                  {!isSingleSet && <div className="participant-type">Sets Won: {setsWon.p2}</div>}
                  <div className="participant-score-large">{p2SetScore}</div>
                  {renderScoreControls("participant2", p2SetScore, p2Players)}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </FloatingCard>
  );
};

export default VolleyballScoreboard;
